import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ProductCard } from "../../components/ProductCard";
import type { Product } from "../../lib/mockData";
import { events, EVENT_NAVIGATE_PRODUCT, EVENT_RECOMMENDATIONS_LOADING, EVENT_RECOMMENDATIONS_UPDATED } from "../events";
import { storeAttribution } from "../../lib/attribution";

interface RecommendationsWidgetProps {
  storeId: string;
  apiBase: string;
}

type StoredRecommendation = {
  id: string | number;
  title: string;
  price: string;
  compareAtPrice?: string;
  currencyDisplay?: string;
  images: string[];
  productUrl?: string;
  // Variant fields (used by ProductCard to resolve variant-aware images)
  variantDimensions?: string[];
  variantCombinations?: Array<{
    values: string[];
    sku?: string;
    url?: string;
    price?: number;
    compare_at_price?: number;
    image_ids?: string[];
  }>;
  pickedVariantValues?: string[];
  imageMeta?: Array<{ id: string; url: string }>;
};

function getStorageKey(storeId: string) {
  return `ww:rec:${storeId}`;
}

function readStoredRecommendations(storeId: string): StoredRecommendation[] {
  try {
    const raw = localStorage.getItem(getStorageKey(storeId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    const list = Array.isArray(parsed)
      ? parsed
      : (parsed && typeof parsed === "object" && Array.isArray((parsed as any).recommendations))
        ? (parsed as any).recommendations
        : [];
    if (!Array.isArray(list)) return [];
    // minimally validate expected fields
    return list
      .filter((p) => p && typeof p === "object")
      .map((p) => {
        const variantDimensions = Array.isArray((p as any).variantDimensions)
          ? (p as any).variantDimensions.map(String)
          : undefined;
        const variantCombinations = Array.isArray((p as any).variantCombinations)
          ? (p as any).variantCombinations
              .filter((c: any) => c && typeof c === "object" && Array.isArray(c.values))
              .map((c: any) => {
                const imageIdsRaw = c?.image_ids;
                const image_ids = Array.isArray(imageIdsRaw)
                  ? imageIdsRaw.filter((id: unknown): id is string => typeof id === "string" && id.length > 0)
                  : undefined;
                return {
                  values: c.values.map(String),
                  sku: typeof c.sku === "string" ? c.sku : undefined,
                  url: typeof c.url === "string" ? c.url : undefined,
                  price: typeof c.price === "number" ? c.price : undefined,
                  compare_at_price: typeof c.compare_at_price === "number" ? c.compare_at_price : undefined,
                  image_ids: image_ids && image_ids.length > 0 ? image_ids : undefined,
                };
              })
          : undefined;
        const pickedVariantValues = Array.isArray((p as any).pickedVariantValues)
          ? (p as any).pickedVariantValues.map(String)
          : undefined;
        const imageMeta = Array.isArray((p as any).imageMeta)
          ? (p as any).imageMeta
              .filter((im: any) => im && typeof im === "object" && typeof im.id === "string" && typeof im.url === "string")
              .map((im: any) => ({ id: String(im.id), url: String(im.url) }))
          : undefined;

        return {
          id: (p as any).id,
          title: String((p as any).title ?? ""),
          price: String((p as any).price ?? ""),
          compareAtPrice: (p as any).compareAtPrice ? String((p as any).compareAtPrice) : undefined,
          currencyDisplay: (p as any).currencyDisplay ? String((p as any).currencyDisplay) : undefined,
          images: Array.isArray((p as any).images) ? (p as any).images.map(String) : [],
          productUrl: (p as any).productUrl ? String((p as any).productUrl) : undefined,
          variantDimensions: variantDimensions && variantDimensions.length > 0 ? variantDimensions : undefined,
          variantCombinations: variantCombinations && variantCombinations.length > 0 ? variantCombinations : undefined,
          pickedVariantValues: pickedVariantValues && pickedVariantValues.length > 0 ? pickedVariantValues : undefined,
          imageMeta: imageMeta && imageMeta.length > 0 ? imageMeta : undefined,
        } as StoredRecommendation;
      })
      .filter((p) => p.id != null && p.title);
  } catch {
    return [];
  }
}

function toRenderableProduct(rec: StoredRecommendation): Product {
  return {
    id: String(rec.id),
    title: rec.title,
    price: rec.price,
    compareAtPrice: rec.compareAtPrice,
    currencyDisplay: rec.currencyDisplay,
    description: "",
    images: rec.images,
    review: { name: "", text: "" },
    productUrl: rec.productUrl,
    variantDimensions: rec.variantDimensions,
    variantCombinations: rec.variantCombinations,
    pickedVariantValues: rec.pickedVariantValues,
    imageMeta: rec.imageMeta,
  };
}

function parseThemeStringToken(rawValue: string): string {
  const trimmed = rawValue.trim();
  if (!trimmed) return "";
  const hasMatchingQuotes =
    (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
    (trimmed.startsWith("'") && trimmed.endsWith("'"));
  if (hasMatchingQuotes) {
    return trimmed.slice(1, -1).trim();
  }
  return trimmed;
}

function readRecommendationsLoadingFromGlobal(): boolean {
  try {
    const raw = (window as typeof window & { __WW__?: { recommendationsLoading?: unknown } }).__WW__;
    return raw?.recommendationsLoading === true;
  } catch {
    return false;
  }
}

export function RecommendationsWidget({ storeId }: RecommendationsWidgetProps) {
  const [recs, setRecs] = useState<StoredRecommendation[]>(() => readStoredRecommendations(storeId));
  const [isLoadingRecommendations, setIsLoadingRecommendations] = useState<boolean>(() => readRecommendationsLoadingFromGlobal());
  const [titleText, setTitleText] = useState<string>("");
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const [isScrollable, setIsScrollable] = useState(false);
  const products = useMemo<Product[]>(() => recs.map(toRenderableProduct), [recs]);
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const trackRef = React.useRef<HTMLDivElement | null>(null);

  const updateEdgeState = useCallback(() => {
    const el = trackRef.current;
    if (!el) {
      setAtStart(true);
      setAtEnd(true);
      setIsScrollable(false);
      return;
    }
    const epsilon = 2;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const hasOverflow = scrollWidth - clientWidth > epsilon;
    setIsScrollable(hasOverflow);
    if (!hasOverflow) {
      setAtStart(true);
      setAtEnd(true);
      return;
    }
    setAtStart(scrollLeft <= epsilon);
    setAtEnd(scrollLeft + clientWidth >= scrollWidth - epsilon);
  }, []);

  useEffect(() => {
    function refresh() {
      setRecs(readStoredRecommendations(storeId));
    }
    setIsLoadingRecommendations(readRecommendationsLoadingFromGlobal());
    // Initial load (in case storeId changes)
    refresh();
    // Listen for in-page update signals
    const onUpdated = () => refresh();
    const onLoading = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (!detail || typeof detail !== "object") return;
      const loading = (detail as { loading?: unknown }).loading;
      if (typeof loading === "boolean") setIsLoadingRecommendations(loading);
    };
    document.addEventListener(EVENT_RECOMMENDATIONS_UPDATED, onUpdated as EventListener);
    document.addEventListener(EVENT_RECOMMENDATIONS_LOADING, onLoading as EventListener);
    // Cross-tab changes
    const onStorage = (e: StorageEvent) => {
      if (e.key === getStorageKey(storeId)) refresh();
    };
    window.addEventListener("storage", onStorage);
    return () => {
      document.removeEventListener(EVENT_RECOMMENDATIONS_UPDATED, onUpdated as EventListener);
      document.removeEventListener(EVENT_RECOMMENDATIONS_LOADING, onLoading as EventListener);
      window.removeEventListener("storage", onStorage);
    };
  }, [storeId]);

  useEffect(() => {
    const readTitleFromTheme = () => {
      if (!rootRef.current) return;
      const raw = window.getComputedStyle(rootRef.current).getPropertyValue("--ww-recommendations-title");
      setTitleText(parseThemeStringToken(raw));
    };

    // Theme CSS can be applied asynchronously after initial mount.
    readTitleFromTheme();
    const rafId = window.requestAnimationFrame(readTitleFromTheme);
    const timeoutId = window.setTimeout(readTitleFromTheme, 200);
    return () => {
      window.cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
    };
  }, [storeId]);

  useEffect(() => {
    updateEdgeState();
    const rafId = window.requestAnimationFrame(updateEdgeState);
    const timeoutId = window.setTimeout(updateEdgeState, 200);
    return () => {
      window.cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
    };
  }, [products.length, isLoadingRecommendations, updateEdgeState]);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(() => updateEdgeState());
      observer.observe(el);
      return () => observer.disconnect();
    }
    const onResize = () => updateEdgeState();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [updateEdgeState, products.length]);

  const onProductSelect = (p: Product) => {
    storeAttribution({
      storeId,
      source: "recommendations_widget",
      productId: p.id,
    });
    const payload = {
      product: {
        id: p.id,
        title: p.title,
        images: p.images,
        price: p.price,
        productUrl: p.productUrl,
      },
      target: "_blank",
    };
    events.emit(EVENT_NAVIGATE_PRODUCT, payload);
  };

  const hasData = products.length > 0;
  const shouldShowProducts = hasData && !isLoadingRecommendations;
  const skeletonCount = 3;

  const scrollByOneProduct = (direction: 1 | -1) => {
    const el = trackRef.current;
    if (!el) return;
    const cards = Array.from(el.querySelectorAll<HTMLElement>(".ww-recommendations-card"));
    if (cards.length === 0) return;
    const currentLeft = el.scrollLeft;
    const epsilon = 2;
    let target: HTMLElement | null = null;

    if (direction === 1) {
      target = cards.find((card) => card.offsetLeft > currentLeft + epsilon) ?? cards[cards.length - 1];
    } else {
      for (let i = cards.length - 1; i >= 0; i -= 1) {
        if (cards[i].offsetLeft < currentLeft - epsilon) {
          target = cards[i];
          break;
        }
      }
      target = target ?? cards[0];
    }

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    el.scrollTo({
      left: target.offsetLeft,
      behavior: prefersReducedMotion ? "auto" : "smooth",
    });
    window.setTimeout(updateEdgeState, 300);
  };

  return (
    <div
      ref={rootRef}
      className="w-full h-full flex flex-col gap-4"
      style={{ minHeight: "var(--ww-recommendations-min-height)" }}
    >
      {titleText ? (
        <h2
          className="text-[18px] font-medium"
          style={{ fontFamily: "var(--font-body)", color: "var(--color-foreground)" }}
        >
          {titleText}
        </h2>
      ) : null}
      <div className="ww-recommendations-carousel">
        <div
          ref={trackRef}
          className="ww-recommendations-track"
          onScroll={updateEdgeState}
        >
          <div className="flex pr-2" style={{ gap: "var(--product-grid-gap)" }}>
            {shouldShowProducts
              ? products.map((p) => (
                  <div key={p.id} className="ww-recommendations-card">
                    <ProductCard
                      product={p}
                      onProductSelect={onProductSelect}
                      size="small"
                    />
                  </div>
                ))
              : Array.from({ length: skeletonCount }).map((_, i) => (
                  <div
                    key={`sk-${i}`}
                    className="ww-recommendations-card rounded-lg overflow-hidden"
                    style={{ background: "#eef2f7" }}
                  >
                    <div style={{ aspectRatio: "var(--product-card-image-aspect, 1 / 1)" }} />
                    <div className="p-6">
                      <div className="h-4 mb-1" />
                      <div className="h-4 mb-4" />
                    </div>
                  </div>
                ))}
          </div>
        </div>
        {isScrollable && shouldShowProducts ? (
          <div className="ww-recommendations-controls">
            <button
              type="button"
              className="ww-recommendations-arrow ww-recommendations-arrow-left"
              onClick={() => scrollByOneProduct(-1)}
              disabled={atStart}
              aria-label="Scroll left"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M15 18L9 12L15 6" />
              </svg>
            </button>
            <button
              type="button"
              className="ww-recommendations-arrow ww-recommendations-arrow-right"
              onClick={() => scrollByOneProduct(1)}
              disabled={atEnd}
              aria-label="Scroll right"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M9 18L15 12L9 6" />
              </svg>
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}


